/**
 * 1.8.0: the rights defaults of a watch folder or a live source, in words (their cards: "Rights: EBU · Restricted ·
 * until 30 days after arrival"). Pure.
 */
import { rightsFields, type FieldDefinition, type MetadataSchemaDefinition, type RightsDates } from '@prism/contracts';

export type RightsRelativeTo = 'arrival' | 'recording';

const AFTER: Record<RightsRelativeTo, string> = { arrival: 'after arrival', recording: 'after the recording starts' };

/** "N hours after arrival", "1 day after the recording starts". */
export function relativeText(n: number, unit: 'hour' | 'day', to: RightsRelativeTo): string {
  return `${n} ${unit}${n === 1 ? '' : 's'} ${AFTER[to]}`;
}

const label = (f: FieldDefinition | undefined, v: unknown): string | null => {
  if (v === undefined || v === null || v === '') return null;
  if (f?.options) return f.options.find((o) => o.value === v)?.label ?? String(v);
  return String(v);
};

/** The summary, or null when the folder or source sets no rights. */
export function rightsDefaultsSummary(
  schema: MetadataSchemaDefinition | undefined, defaults: Record<string, unknown> | undefined, dates: RightsDates | undefined, to: RightsRelativeTo,
): string | null {
  if (!schema) return null;
  const f = rightsFields(schema);
  const d = defaults ?? {};
  const parts: string[] = [];
  const source = label(f.rights_source, f.rights_source && d[f.rights_source.id]);
  const holder = label(f.rights_holder, f.rights_holder && d[f.rights_holder.id]);
  const status = label(f.rights_status, f.rights_status && d[f.rights_status.id]);
  if (source) parts.push(source);
  else if (holder) parts.push(holder);
  if (status) parts.push(status);
  if (dates?.fromAfterHours !== undefined) parts.push(`from ${relativeText(dates.fromAfterHours, 'hour', to)}`);
  else if (f.rights_from && d[f.rights_from.id]) parts.push(`from ${String(d[f.rights_from.id]).replace('T', ' ').slice(0, 16)}`);
  if (dates?.untilAfterDays !== undefined) parts.push(`until ${relativeText(dates.untilAfterDays, 'day', to)}`);
  else if (f.rights_until && d[f.rights_until.id]) parts.push(`until ${String(d[f.rights_until.id])}`);
  if (!parts.length) {
    const any = Object.values(f).some((x) => x && d[x.id] !== undefined && d[x.id] !== '');
    return any ? 'Rights: set' : null;
  }
  return `Rights: ${parts.join(' · ')}`;
}

/** The ids of the model's rights fields (they leave the generic Default metadata grid while rights are on). */
export const rightsFieldIds = (schema: MetadataSchemaDefinition | undefined): Set<string> =>
  new Set(schema ? Object.values(rightsFields(schema)).map((x) => x!.id) : []);
